/**
 * The desk and tablet chips in the top bar.
 *
 * Always visible during a shift, always live. A receptionist has to be able to
 * answer "which desk am I on, and is my tablet awake" without navigating
 * anywhere — because when a guest is standing there, navigating is a cost.
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, MonitorPlay, Radio, Tablet, X } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { connectSocket, disconnectSocket, on } from '@/lib/socket'
import type { Desk } from '@/lib/types'
import { Button, StatusDot } from '@/components/ui/primitives'
import { useToast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'

export function DeskChip() {
  const { session, refresh, can } = useAuth()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [open, setOpen] = useState(false)

  const desks = useQuery({
    queryKey: ['desks'],
    queryFn: () => api.get<Desk[]>('/desks'),
    enabled: can('desks:read'),
    refetchInterval: 45_000,
  })

  useEffect(() => {
    const off = on('device.status_changed', () => {
      void queryClient.invalidateQueries({ queryKey: ['desks'] })
    })
    return off
  }, [queryClient])

  /**
   * Rejoin the realtime rooms for the desk we now hold.
   *
   * The server puts a socket into `desk:<id>` at the *handshake*, from the
   * assignment that existed at that moment — and there is no "change desk"
   * message. So a receptionist who signed in and then claimed a desk stayed in
   * no desk room at all: every capture event for their own desk was addressed
   * to a room they were not in, and the inbox moved only when the
   * twenty-second poll happened to land. Reconnecting is the whole fix, and it
   * is what `selectProperty` already does for the same reason.
   */
  const rejoin = () => {
    disconnectSocket()
    connectSocket()
  }

  const claim = useMutation({
    mutationFn: (deskId: string) => api.post(`/desks/${deskId}/claim`),
    onSuccess: async () => {
      setOpen(false)
      await refresh()
      rejoin()
      void queryClient.invalidateQueries()
      toast.success('Desk claimed')
    },
    onError: (error: Error) => toast.error('Could not claim that desk', error.message),
  })

  const release = useMutation({
    mutationFn: (deskId: string) => api.post(`/desks/${deskId}/release`),
    onSuccess: async () => {
      setOpen(false)
      await refresh()
      rejoin()
      void queryClient.invalidateQueries()
      toast.success('Desk released')
    },
    onError: (error: Error) => toast.error('Could not release the desk', error.message),
  })

  const command = useMutation({
    mutationFn: ({ deviceId, action }: { deviceId: string; action: string }) =>
      api.post<{ message?: string }>(`/devices/${deviceId}/command`, {
        command: action,
        params: {},
      }),
    onSuccess: (result: { message?: string }) => toast.success(result.message ?? 'Sent'),
  })

  if (!can('desks:occupy') && !can('desks:read')) return null

  const mine = session?.desk ?? null
  const myDesk = desks.data?.find((desk) => desk.id === mine?.id) ?? null
  const device = myDesk?.device ?? null
  // A receptionist is only offered desks they could sit at — who is at the
  // others is not their business. Administrators (desks:write) see every desk
  // and who holds it, as on Property & Desks.
  const seesAll = can('desks:write')
  const offered = (desks.data ?? []).filter(
    (desk) => seesAll || desk.id === mine?.id || !desk.occupied_by,
  )

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          'flex items-center gap-2 rounded-full border px-3 py-1.5 text-[13px] transition-colors',
          mine
            ? 'border-[var(--brand)] bg-[var(--brand-subtle)] text-[var(--brand)]'
            : 'border-[var(--border-strong)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-2)]',
        )}
      >
        <Radio className="h-3.5 w-3.5" aria-hidden />
        <span className="font-medium">{mine ? mine.name : 'No desk'}</span>
        {device && (
          <>
            <span className="text-[var(--border)]" aria-hidden>
              |
            </span>
            <StatusDot
              status={device.online ? device.status : 'offline'}
              label={device.name}
            />
          </>
        )}
        <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className="fade-in absolute end-0 top-full z-50 mt-2 w-[19rem] rounded-[14px] border border-[var(--border-strong)] bg-[var(--surface)] p-2 shadow-[var(--shadow-lg)]"
          >
            <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
              Reception desks
            </div>

            {desks.isPending ? (
              <p className="px-2 py-3 text-[13px] text-[var(--text-muted)]">Loading…</p>
            ) : desks.isError ? (
              <p className="px-2 py-3 text-[13px] text-[var(--danger)]">
                Could not load desks. {desks.error.message}
              </p>
            ) : (desks.data ?? []).length === 0 ? (
              <p className="px-2 py-3 text-[13px] text-[var(--text-muted)]">
                No desks exist yet. An administrator creates them in Property &amp; Desks.
              </p>
            ) : offered.length === 0 ? (
              <p className="px-2 py-3 text-[13px] text-[var(--text-muted)]">
                Every desk is in use. One appears here as soon as it is released.
              </p>
            ) : (
              <ul className="flex flex-col gap-0.5">
                {offered.map((desk) => {
                  const isMine = desk.id === mine?.id
                  const takenByOther = Boolean(desk.occupied_by) && !isMine
                  return (
                    <li key={desk.id}>
                      <button
                        role="menuitem"
                        disabled={takenByOther || !can('desks:occupy')}
                        onClick={() => (isMine ? release.mutate(desk.id) : claim.mutate(desk.id))}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-[8px] px-2 py-2 text-start text-[13px] transition-colors',
                          'disabled:cursor-not-allowed disabled:opacity-55',
                          isMine
                            ? 'bg-[var(--brand-subtle)] text-[var(--brand)]'
                            : 'hover:bg-[var(--surface-2)]',
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{desk.name}</span>
                          <span className="block text-[11px] text-[var(--text-muted)]">
                            {isMine
                              ? 'You are here'
                              : desk.occupied_by
                                ? `${desk.occupied_by.display_name} is here`
                                : 'Free'}
                            {desk.device ? ` · ${desk.device.name}` : ' · no tablet'}
                          </span>
                        </span>
                        {isMine && <X className="h-3.5 w-3.5 shrink-0" aria-label="Release" />}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}

            {device && can('devices:command') && (
              <>
                <div className="mt-2 border-t border-[var(--border)] px-2 pt-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                  {device.name}
                </div>
                <div className="flex flex-wrap gap-1.5 p-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Tablet className="h-3.5 w-3.5" />}
                    onClick={() => command.mutate({ deviceId: device.id, action: 'identify' })}
                  >
                    Identify
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<MonitorPlay className="h-3.5 w-3.5" />}
                    onClick={() => command.mutate({ deviceId: device.id, action: 'show_signage' })}
                  >
                    Show signage
                  </Button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
